import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CircleGauge,
  Clock3,
  Filter,
  Layers3,
  Plus,
  Search,
  Sparkles,
  Target,
  Users,
  Workflow,
} from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { TRUEVANCE_BRAND_NAME, TruevanceBrandHeader, useTruevanceBranding } from "./TruevanceBranding";
import {
  assessmentPriorityOptions,
  assessmentCategoryOptions,
  assessmentStatusOptions,
  calculateTruevanceMetrics,
  consultingOpportunityStageOptions,
  consultingOpportunityStatusOptions,
  formatCurrency,
  formatDisplayDate,
  formatPercent,
  initializeTruevanceStore,
  projectPhaseOptions,
  projectStatusOptions,
  recommendationStatusOptions,
  roadmapHorizonOptions,
  roadmapStatusOptions,
  truevanceOperationalLinks,
  type BusinessAssessmentRecord,
  type ConsultingOpportunityRecord,
  type ConsultingRecommendationRecord,
  type ImplementationRoadmapRecord,
  type ConsultingProjectRecord,
  type TruevanceStore,
} from "./TruevanceConnectData";
import {
  appendTruevanceAssessmentEvent,
  appendTruevanceOpportunityEvent,
  appendTruevanceRecommendationEvent,
  appendTruevanceRoadmapEvent,
  appendTruevanceProjectEvent,
  createTruevanceAssessment,
  createTruevanceOpportunity,
  createTruevanceRecommendation,
  createTruevanceRoadmap,
  createTruevanceProject,
  loadTruevanceWorkspace,
  updateTruevanceAssessment,
  updateTruevanceOpportunity,
  updateTruevanceRecommendation,
  updateTruevanceRoadmap,
  updateTruevanceProject,
} from "./TruevanceConnectApi";

const workspaceNavigation = [
  { label: TRUEVANCE_BRAND_NAME, to: "/admin/truevance-business-center" },
  { label: "Consulting Pipeline", to: "/admin/consulting-pipeline" },
  { label: "Assessment Framework", to: "/admin/assessment-framework" },
  { label: "Client Engagement Dashboard", to: "/admin/client-engagement-dashboard" },
  { label: "Service Categories", to: "/admin/service-categories" },
  { label: "Platform Architecture", to: "/admin/platform-architecture" },
  { label: "Opportunity Management", to: "/admin/truevance-opportunities" },
  { label: "Assessment Records", to: "/admin/truevance-assessments" },
  { label: "Consulting Projects", to: "/admin/truevance-projects" },
];

function useTruevanceWorkspaceStore() {
  const [store, setStore] = useState<TruevanceStore | null>(null);
  const [loadStatus, setLoadStatus] = useState<"loading" | "ready" | "error">("loading");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadStatus("loading");
    setLoadError(null);
    loadTruevanceWorkspace()
      .then((data) => {
        if (!cancelled) {
          setStore(data.store);
          setLoadStatus("ready");
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setStore(import.meta.env.DEV ? initializeTruevanceStore() : null);
          setLoadError(error instanceof Error ? error.message : "Unable to load the Truevance workspace.");
          setLoadStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return [store, setStore, loadStatus, loadError] as const;
}

export function TruevanceOpportunityManagementPage() {
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState<"All" | (typeof consultingOpportunityStageOptions)[number]>("All");
  const [statusFilter, setStatusFilter] = useState<"All" | (typeof consultingOpportunityStatusOptions)[number]>("All");
  const [form, setForm] = useState(createBlankOpportunityForm());

  useEffect(() => {
    document.title = `Opportunity Management | ${TRUEVANCE_BRAND_NAME}`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", "Persistent consulting opportunity workspace for Truevance Connect Business Center.");
  }, []);

  useTruevanceBranding("Opportunity Management", "Persistent consulting opportunity workspace for Truevance Connect Business Center.");

  const metrics = useMemo(() => (store ? calculateTruevanceMetrics(store) : null), [store]);

  const filteredOpportunities = useMemo(() => {
    if (!store) return [];
    const normalizedQuery = query.trim().toLowerCase();
    return store.opportunities.filter((record) => {
      const matchesStage = stageFilter === "All" || record.currentStage === stageFilter;
      const matchesStatus = statusFilter === "All" || record.status === statusFilter;
      const haystack = [
        record.opportunityId,
        record.companyName,
        record.contactName,
        record.email,
        record.industry,
        record.leadSource,
        record.opportunityType,
        record.assignedConsultant,
        record.nextAction,
        record.notes,
      ]
        .join(" ")
        .toLowerCase();
      return matchesStage && matchesStatus && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [query, stageFilter, statusFilter, store]);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleCreateOpportunity(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const created = await createTruevanceOpportunity(form);
    if (!created?.opportunity) return;
    setStore((current) =>
      current
        ? {
            ...current,
            opportunities: [created.opportunity, ...current.opportunities],
          }
        : { opportunities: [created.opportunity], assessments: [], recommendations: [], roadmaps: [], projects: [] },
    );
    setForm(createBlankOpportunityForm());
    navigate(`/admin/truevance-opportunities/${created.opportunity.id}`);
  }

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Opportunity Management"
        description="We could not load the consulting opportunity workspace from the production data source."
        detail={loadError || "The workspace fetch failed before the records could load."}
      />
    );
  }
  if (loadStatus === "loading" || !store || !metrics) {
    return <LoadingShell title="Opportunity Management" description="Loading consulting opportunity records." />;
  }

  const stageCounts = consultingOpportunityStageOptions.map((stage) => ({
    label: stage,
    value: store.opportunities.filter((item) => item.currentStage === stage).length,
  }));

  return (
    <WorkspaceShell
      title="Opportunity Management"
      eyebrow={TRUEVANCE_BRAND_NAME}
      description="Track consulting opportunities from new inquiry through completion or loss with persistent records and actionable next steps."
      icon={Target}
      metrics={[
        { label: "Open Opportunities", value: metrics.openOpportunities, note: "Current active consulting opportunities." },
        { label: "Pipeline Value", value: formatCurrency(metrics.pipelineValue), note: "Weighted by opportunity probability." },
        { label: "Win Rate", value: formatPercent(metrics.winRate), note: "Closed opportunities won over closed total." },
        { label: "Expected Revenue", value: formatCurrency(store.opportunities.reduce((sum, item) => sum + item.estimatedRevenue, 0)), note: "Total raw opportunity value." },
      ]}
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/truevance-connect" className="admin-secondary-button focus-ring">
            Back to Business Center
          </Link>
          <Link to="/admin/truevance-assessments" className="admin-secondary-button focus-ring">
            Assessment Records
          </Link>
          <Link to="/admin/truevance-projects" className="admin-secondary-button focus-ring">
            Consulting Projects
          </Link>
        </div>
      }
    >
      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading
          title="Create Opportunity"
          subtitle="Capture the company, contact, lead source, opportunity type, value, next action, and current stage in one persistent record."
        />
        <form onSubmit={handleCreateOpportunity} className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <TextField label="Company Name" required value={form.companyName} onChange={(value) => updateForm("companyName", value)} />
          <TextField label="Contact Name" value={form.contactName} onChange={(value) => updateForm("contactName", value)} />
          <TextField label="Email" type="email" value={form.email} onChange={(value) => updateForm("email", value)} />
          <TextField label="Phone" type="tel" value={form.phone} onChange={(value) => updateForm("phone", value)} />
          <TextField label="Industry" value={form.industry} onChange={(value) => updateForm("industry", value)} />
          <TextField label="Lead Source" value={form.leadSource} onChange={(value) => updateForm("leadSource", value)} />
          <TextField label="Opportunity Type" value={form.opportunityType} onChange={(value) => updateForm("opportunityType", value)} />
          <TextField
            label="Estimated Revenue"
            type="number"
            value={String(form.estimatedRevenue)}
            onChange={(value) => updateForm("estimatedRevenue", Number(value || 0))}
          />
          <TextField
            label="Probability %"
            type="number"
            value={String(form.probability)}
            onChange={(value) => updateForm("probability", Number(value || 0))}
          />
          <SelectField
            label="Current Stage"
            value={form.currentStage}
            options={consultingOpportunityStageOptions}
            onChange={(value) => updateForm("currentStage", value as (typeof consultingOpportunityStageOptions)[number])}
          />
          <TextField label="Assigned Consultant" value={form.assignedConsultant} onChange={(value) => updateForm("assignedConsultant", value)} />
          <TextField label="Next Action" value={form.nextAction} onChange={(value) => updateForm("nextAction", value)} />
          <TextField label="Follow-Up Date" type="date" value={form.followUpDate} onChange={(value) => updateForm("followUpDate", value)} />
          <SelectField
            label="Status"
            value={form.status}
            options={consultingOpportunityStatusOptions}
            onChange={(value) => updateForm("status", value as (typeof consultingOpportunityStatusOptions)[number])}
          />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Notes" value={form.notes} onChange={(value) => updateForm("notes", value)} />
          <div className="flex justify-end md:col-span-2 xl:col-span-3">
            <button type="submit" className="admin-primary-button focus-ring">
              <Plus className="size-4" aria-hidden="true" />
              Save Opportunity
            </button>
          </div>
        </form>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1.1fr_.9fr]">
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Search and Filter" subtitle="Narrow the opportunity list by stage, status, or keyword." />
          <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_220px_220px]">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search company, contact, source, or notes"
                className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold"
              />
            </label>
            <SelectField
              label="Stage"
              value={stageFilter}
              options={["All", ...consultingOpportunityStageOptions]}
              onChange={(value) => setStageFilter(value as "All" | (typeof consultingOpportunityStageOptions)[number])}
            />
            <SelectField
              label="Status"
              value={statusFilter}
              options={["All", ...consultingOpportunityStatusOptions]}
              onChange={(value) => setStatusFilter(value as "All" | (typeof consultingOpportunityStatusOptions)[number])}
            />
          </div>
        </article>

        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Pipeline Stages" subtitle="The consulting pipeline remains visible while records persist underneath it." />
          <div className="mt-5 grid gap-3">
            {stageCounts.map((stage) => (
              <div key={stage.label} className="flex items-center justify-between rounded-lg border border-[#FF6A2B]/18 bg-white/5 px-4 py-3">
                <span className="text-sm font-bold text-[#fff9ef]">{stage.label}</span>
                <span className="rounded-full border border-[#FF6A2B]/20 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                  {stage.value}
                </span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Opportunity Records" subtitle="Open a record to review detail history, update the record, and log timeline activity." />
        <div className="mt-5 grid gap-4">
          {filteredOpportunities.length ? (
            filteredOpportunities.map((record) => <OpportunityCard key={record.id} record={record} />)
          ) : (
            <EmptyState
              title="No opportunities match the current filter"
              description="Your first Truevance opportunity will appear here once a record is created."
            />
          )}
        </div>
      </section>
    </WorkspaceShell>
  );
}

export function TruevanceOpportunityDetailPage() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();

  useEffect(() => {
    document.title = `Opportunity Detail | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const record = useMemo(
    () => (store && opportunityId ? store.opportunities.find((item) => item.id === opportunityId) || null : null),
    [opportunityId, store],
  );

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Opportunity Detail"
        description="We could not load the selected consulting opportunity record."
        detail={loadError || "The record could not be loaded from the Truevance store."}
      />
    );
  }
  if (loadStatus === "loading" || !store) {
    return <LoadingShell title="Opportunity Detail" description="Loading opportunity record." />;
  }

  if (!record) {
    return (
      <NotFoundShell
        title="Opportunity not found"
        description="The requested consulting opportunity does not exist in the current Truevance store."
        backHref="/admin/truevance-opportunities"
        backLabel="Back to Opportunities"
      />
    );
  }

  const currentRecord = record;

  async function persist(nextRecord: ConsultingOpportunityRecord) {
    const response = await updateTruevanceOpportunity(nextRecord.id, nextRecord);
    if (!response?.opportunity) return;
    setStore((current) =>
      current
        ? {
            ...current,
            opportunities: current.opportunities.map((item) => (item.id === response.opportunity.id ? response.opportunity : item)),
          }
        : current,
    );
  }

  function updateField<K extends keyof ConsultingOpportunityRecord>(key: K, value: ConsultingOpportunityRecord[K]) {
    if (!currentRecord) return;
    void persist({ ...currentRecord, [key]: value, updatedAt: new Date().toISOString() });
  }

  function addEvent(event: FormEvent<HTMLFormElement>, detailForm: { title: string; detail: string }) {
    event.preventDefault();
    if (!detailForm.title.trim()) return;
    void appendTruevanceOpportunityEvent(currentRecord.id, { title: detailForm.title, detail: detailForm.detail }).then((response) => {
      if (!response?.opportunity) return;
      setStore((current) =>
        current
          ? {
              ...current,
              opportunities: current.opportunities.map((item) => (item.id === response.opportunity.id ? response.opportunity : item)),
            }
          : current,
      );
    });
  }

  return (
    <RecordDetailShell
      title="Opportunity Detail"
      eyebrow={TRUEVANCE_BRAND_NAME}
      description="Review the opportunity record, update key milestones, and keep the engagement timeline current."
      backHref="/admin/truevance-opportunities"
      backLabel="Back to Opportunities"
      recordLabel={record.opportunityId}
    >
      <div className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Opportunity Information" subtitle="Keep the operational record accurate and current." />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <TextField label="Company Name" value={record.companyName} onChange={(value) => updateField("companyName", value)} />
            <TextField label="Contact Name" value={record.contactName} onChange={(value) => updateField("contactName", value)} />
            <TextField label="Email" type="email" value={record.email} onChange={(value) => updateField("email", value)} />
            <TextField label="Phone" type="tel" value={record.phone} onChange={(value) => updateField("phone", value)} />
            <TextField label="Industry" value={record.industry} onChange={(value) => updateField("industry", value)} />
            <TextField label="Lead Source" value={record.leadSource} onChange={(value) => updateField("leadSource", value)} />
            <TextField label="Opportunity Type" value={record.opportunityType} onChange={(value) => updateField("opportunityType", value)} />
            <TextField
              label="Estimated Revenue"
              type="number"
              value={String(record.estimatedRevenue)}
              onChange={(value) => updateField("estimatedRevenue", Number(value || 0))}
            />
            <TextField label="Probability %" type="number" value={String(record.probability)} onChange={(value) => updateField("probability", Number(value || 0))} />
            <SelectField
              label="Current Stage"
              value={record.currentStage}
              options={consultingOpportunityStageOptions}
              onChange={(value) => updateField("currentStage", value as ConsultingOpportunityRecord["currentStage"])}
            />
            <TextField label="Assigned Consultant" value={record.assignedConsultant} onChange={(value) => updateField("assignedConsultant", value)} />
            <TextField label="Next Action" value={record.nextAction} onChange={(value) => updateField("nextAction", value)} />
            <TextField label="Follow-Up Date" type="date" value={record.followUpDate} onChange={(value) => updateField("followUpDate", value)} />
            <SelectField
              label="Status"
              value={record.status}
              options={consultingOpportunityStatusOptions}
              onChange={(value) => updateField("status", value as ConsultingOpportunityRecord["status"])}
            />
            <TextAreaField className="md:col-span-2" label="Notes" value={record.notes} onChange={(value) => updateField("notes", value)} />
          </div>
        </article>

        <TimelinePanel
          title="Opportunity Timeline"
          subtitle="Log activity, stage updates, and follow-up notes against this record."
          events={record.timelineEvents}
          onAdd={addEvent}
        />
      </div>
    </RecordDetailShell>
  );
}

export function TruevanceAssessmentManagementPage() {
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | (typeof assessmentStatusOptions)[number]>("All");
  const [form, setForm] = useState(createBlankAssessmentForm());

  useEffect(() => {
    document.title = `Assessment Records | ${TRUEVANCE_BRAND_NAME}`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", "Persistent business assessment records for Truevance Connect Business Center.");
  }, []);

  useTruevanceBranding("Assessment Records", "Persistent business assessment records for Truevance Connect Business Center.");

  const metrics = useMemo(() => (store ? calculateTruevanceMetrics(store) : null), [store]);

  const filteredAssessments = useMemo(() => {
    if (!store) return [];
    const normalizedQuery = query.trim().toLowerCase();
    return store.assessments.filter((record) => {
      const matchesStatus = statusFilter === "All" || record.status === statusFilter;
      const haystack = [
        record.assessmentId,
        record.client,
        record.assessmentDate,
        record.businessChallenges,
        record.operationalChallenges,
        record.customerExperienceChallenges,
        record.technologyChallenges,
        record.recommendations,
        record.assignedConsultant,
        record.followUpActions,
      ]
        .join(" ")
        .toLowerCase();
      return matchesStatus && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [query, statusFilter, store]);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleCreateAssessment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const created = await createTruevanceAssessment(form);
    if (!created?.assessment) return;
    setStore((current) =>
      current
        ? {
            ...current,
            assessments: [created.assessment, ...current.assessments],
          }
        : { opportunities: [], assessments: [created.assessment], recommendations: [], roadmaps: [], projects: [] },
    );
    setForm(createBlankAssessmentForm());
    navigate(`/admin/truevance-assessments/${created.assessment.id}`);
  }

  if (!store || !metrics) {
    return <LoadingShell title="Assessment Records" description="Loading consulting assessment records." />;
  }

  return (
    <WorkspaceShell
      title="Assessment Records"
      eyebrow={TRUEVANCE_BRAND_NAME}
      description="Document business challenges, operational bottlenecks, recommendations, and follow-up actions in one persistent workspace."
      icon={Sparkles}
      metrics={[
        { label: "Active Assessments", value: metrics.activeAssessments, note: "Assessments currently in progress." },
        { label: "Open Opportunities", value: metrics.openOpportunities, note: "Pipeline records that may convert into assessments." },
        { label: "Average Project Value", value: formatCurrency(metrics.averageProjectValue), note: "Average revenue across consulting projects." },
        { label: "Consulting Revenue", value: formatCurrency(metrics.consultingRevenue), note: "Project revenue captured in the platform." },
      ]}
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/truevance-connect" className="admin-secondary-button focus-ring">
            Back to Business Center
          </Link>
          <Link to="/admin/truevance-opportunities" className="admin-secondary-button focus-ring">
            Opportunity Management
          </Link>
          <Link to="/admin/truevance-projects" className="admin-secondary-button focus-ring">
            Consulting Projects
          </Link>
        </div>
      }
    >
      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading
          title="Create Assessment"
          subtitle="Record the business challenges, opportunities, recommendations, priority, and follow-up actions for each engagement."
        />
        <form onSubmit={handleCreateAssessment} className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <TextField label="Client" required value={form.client} onChange={(value) => updateForm("client", value)} />
          <TextField label="Assessment Date" type="date" value={form.assessmentDate} onChange={(value) => updateForm("assessmentDate", value)} />
          <SelectField
            label="Priority Level"
            value={form.priorityLevel}
            options={assessmentPriorityOptions}
            onChange={(value) => updateForm("priorityLevel", value as (typeof assessmentPriorityOptions)[number])}
          />
          <TextField label="Assigned Consultant" value={form.assignedConsultant} onChange={(value) => updateForm("assignedConsultant", value)} />
          <TextField label="Estimated Impact" value={form.estimatedImpact} onChange={(value) => updateForm("estimatedImpact", value)} />
          <SelectField
            label="Status"
            value={form.status}
            options={assessmentStatusOptions}
            onChange={(value) => updateForm("status", value as (typeof assessmentStatusOptions)[number])}
          />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Business Challenges" value={form.businessChallenges} onChange={(value) => updateForm("businessChallenges", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Operational Challenges" value={form.operationalChallenges} onChange={(value) => updateForm("operationalChallenges", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Customer Experience Challenges" value={form.customerExperienceChallenges} onChange={(value) => updateForm("customerExperienceChallenges", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Technology Challenges" value={form.technologyChallenges} onChange={(value) => updateForm("technologyChallenges", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Recommendations" value={form.recommendations} onChange={(value) => updateForm("recommendations", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Follow-Up Actions" value={form.followUpActions} onChange={(value) => updateForm("followUpActions", value)} />
          <div className="flex justify-end md:col-span-2 xl:col-span-3">
            <button type="submit" className="admin-primary-button focus-ring">
              <Plus className="size-4" aria-hidden="true" />
              Save Assessment
            </button>
          </div>
        </form>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Search and Filter" subtitle="Narrow the assessment list by status or search term." />
        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_220px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search client, challenge, recommendation, or consultant"
              className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold"
            />
          </label>
          <SelectField
            label="Status"
            value={statusFilter}
            options={["All", ...assessmentStatusOptions]}
            onChange={(value) => setStatusFilter(value as "All" | (typeof assessmentStatusOptions)[number])}
          />
        </div>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Assessment Records" subtitle="Open any record to review and update the detail view." />
        <div className="mt-5 grid gap-4">
          {filteredAssessments.length ? (
            filteredAssessments.map((record) => <AssessmentCard key={record.id} record={record} />)
          ) : (
            <EmptyState
              title="No assessments match the current filter"
              description="Your first Truevance assessment will appear here once a record is created."
            />
          )}
        </div>
      </section>
    </WorkspaceShell>
  );
}

export function TruevanceAssessmentDetailPage() {
  const { assessmentId } = useParams();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();

  useEffect(() => {
    document.title = `Assessment Detail | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const record = useMemo(
    () => (store && assessmentId ? store.assessments.find((item) => item.id === assessmentId) || null : null),
    [assessmentId, store],
  );

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Assessment Detail"
        description="We could not load the selected business assessment record."
        detail={loadError || "The record could not be loaded from the Truevance store."}
      />
    );
  }
  if (loadStatus === "loading" || !store) {
    return <LoadingShell title="Assessment Detail" description="Loading assessment record." />;
  }

  if (!record) {
    return (
      <NotFoundShell
        title="Assessment not found"
        description="The requested business assessment does not exist in the current Truevance store."
        backHref="/admin/truevance-assessments"
        backLabel="Back to Assessments"
      />
    );
  }

  const currentRecord = record;

  async function persist(nextRecord: BusinessAssessmentRecord) {
    const response = await updateTruevanceAssessment(nextRecord.id, nextRecord);
    if (!response?.assessment) return;
    setStore((current) =>
      current
        ? {
            ...current,
            assessments: current.assessments.map((item) => (item.id === response.assessment.id ? response.assessment : item)),
          }
        : current,
    );
  }

  function updateField<K extends keyof BusinessAssessmentRecord>(key: K, value: BusinessAssessmentRecord[K]) {
    if (!currentRecord) return;
    void persist({ ...currentRecord, [key]: value, updatedAt: new Date().toISOString() });
  }

  function addEvent(event: FormEvent<HTMLFormElement>, detailForm: { title: string; detail: string }) {
    event.preventDefault();
    if (!detailForm.title.trim()) return;
    void appendTruevanceAssessmentEvent(currentRecord.id, { title: detailForm.title, detail: detailForm.detail }).then((response) => {
      if (!response?.assessment) return;
      setStore((current) =>
        current
          ? {
              ...current,
              assessments: current.assessments.map((item) => (item.id === response.assessment.id ? response.assessment : item)),
            }
          : current,
      );
    });
  }

  return (
    <RecordDetailShell
      title="Assessment Detail"
      eyebrow={TRUEVANCE_BRAND_NAME}
      description="Capture the assessment narrative, recommendations, and follow-up actions with a persistent timeline."
      backHref="/admin/truevance-assessments"
      backLabel="Back to Assessments"
      recordLabel={record.assessmentId}
    >
      <div className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Assessment Information" subtitle="Keep the assessment record current." />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <TextField label="Client" value={record.client} onChange={(value) => updateField("client", value)} />
            <TextField label="Assessment Date" type="date" value={record.assessmentDate} onChange={(value) => updateField("assessmentDate", value)} />
            <SelectField
              label="Priority Level"
              value={record.priorityLevel}
              options={assessmentPriorityOptions}
              onChange={(value) => updateField("priorityLevel", value as BusinessAssessmentRecord["priorityLevel"])}
            />
            <TextField label="Assigned Consultant" value={record.assignedConsultant} onChange={(value) => updateField("assignedConsultant", value)} />
            <TextField label="Estimated Impact" value={record.estimatedImpact} onChange={(value) => updateField("estimatedImpact", value)} />
            <SelectField
              label="Status"
              value={record.status}
              options={assessmentStatusOptions}
              onChange={(value) => updateField("status", value as BusinessAssessmentRecord["status"])}
            />
            <TextAreaField className="md:col-span-2" label="Business Challenges" value={record.businessChallenges} onChange={(value) => updateField("businessChallenges", value)} />
            <TextAreaField className="md:col-span-2" label="Operational Challenges" value={record.operationalChallenges} onChange={(value) => updateField("operationalChallenges", value)} />
            <TextAreaField className="md:col-span-2" label="Customer Experience Challenges" value={record.customerExperienceChallenges} onChange={(value) => updateField("customerExperienceChallenges", value)} />
            <TextAreaField className="md:col-span-2" label="Technology Challenges" value={record.technologyChallenges} onChange={(value) => updateField("technologyChallenges", value)} />
            <TextAreaField className="md:col-span-2" label="Recommendations" value={record.recommendations} onChange={(value) => updateField("recommendations", value)} />
            <TextAreaField className="md:col-span-2" label="Follow-Up Actions" value={record.followUpActions} onChange={(value) => updateField("followUpActions", value)} />
          </div>
        </article>

        <TimelinePanel
          title="Assessment Timeline"
          subtitle="Log assessment activity and recommendation milestones."
          events={record.timelineEvents}
          onAdd={addEvent}
        />
      </div>
    </RecordDetailShell>
  );
}

export function TruevanceProjectManagementPage() {
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();
  const [query, setQuery] = useState("");
  const [phaseFilter, setPhaseFilter] = useState<"All" | (typeof projectPhaseOptions)[number]>("All");
  const [statusFilter, setStatusFilter] = useState<"All" | (typeof projectStatusOptions)[number]>("All");
  const [form, setForm] = useState(createBlankProjectForm());

  useEffect(() => {
    document.title = `Consulting Projects | ${TRUEVANCE_BRAND_NAME}`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", "Persistent consulting project workspace for Truevance Connect Business Center.");
  }, []);

  useTruevanceBranding("Consulting Projects", "Persistent consulting project workspace for Truevance Connect Business Center.");

  const metrics = useMemo(() => (store ? calculateTruevanceMetrics(store) : null), [store]);

  const filteredProjects = useMemo(() => {
    if (!store) return [];
    const normalizedQuery = query.trim().toLowerCase();
    return store.projects.filter((record) => {
      const matchesPhase = phaseFilter === "All" || record.currentPhase === phaseFilter;
      const matchesStatus = statusFilter === "All" || record.status === statusFilter;
      const haystack = [
        record.projectId,
        record.client,
        record.assignedTeam,
        record.notes,
        record.currentPhase,
      ]
        .join(" ")
        .toLowerCase();
      return matchesPhase && matchesStatus && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [query, phaseFilter, statusFilter, store]);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleCreateProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const created = await createTruevanceProject(form);
    if (!created?.project) return;
    setStore((current) =>
      current
        ? {
            ...current,
            projects: [created.project, ...current.projects],
          }
        : { opportunities: [], assessments: [], recommendations: [], roadmaps: [], projects: [created.project] },
    );
    setForm(createBlankProjectForm());
    navigate(`/admin/truevance-projects/${created.project.id}`);
  }

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Consulting Projects"
        description="We could not load the consulting project workspace from the production data source."
        detail={loadError || "The workspace fetch failed before the project records could load."}
      />
    );
  }
  if (loadStatus === "loading" || !store || !metrics) {
    return <LoadingShell title="Consulting Projects" description="Loading consulting project records." />;
  }

  return (
    <WorkspaceShell
      title="Consulting Projects"
      eyebrow={TRUEVANCE_BRAND_NAME}
      description="Track consulting engagements through discovery, assessment, recommendations, implementation, optimization, and completion."
      icon={Workflow}
      metrics={[
        { label: "Active Projects", value: metrics.activeProjects, note: "Projects currently in delivery." },
        { label: "Consulting Revenue", value: formatCurrency(metrics.consultingRevenue), note: "Revenue captured across consulting projects." },
        { label: "Average Project Value", value: formatCurrency(metrics.averageProjectValue), note: "Average value per project record." },
        { label: "Project Completion", value: formatPercent(metrics.projectCompletionRate), note: "Average completion across active project records." },
      ]}
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/truevance-connect" className="admin-secondary-button focus-ring">
            Back to Business Center
          </Link>
          <Link to="/admin/truevance-opportunities" className="admin-secondary-button focus-ring">
            Opportunity Management
          </Link>
          <Link to="/admin/truevance-assessments" className="admin-secondary-button focus-ring">
            Assessment Records
          </Link>
        </div>
      }
    >
      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading
          title="Create Project"
          subtitle="Record the client, phase, completion percentage, revenue, team, and status for each consulting project."
        />
        <form onSubmit={handleCreateProject} className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <TextField label="Client" required value={form.client} onChange={(value) => updateForm("client", value)} />
          <TextField label="Start Date" type="date" value={form.startDate} onChange={(value) => updateForm("startDate", value)} />
          <SelectField
            label="Current Phase"
            value={form.currentPhase}
            options={projectPhaseOptions}
            onChange={(value) => updateForm("currentPhase", value as (typeof projectPhaseOptions)[number])}
          />
          <TextField
            label="Completion %"
            type="number"
            value={String(form.completionPercent)}
            onChange={(value) => updateForm("completionPercent", Number(value || 0))}
          />
          <TextField label="Revenue" type="number" value={String(form.revenue)} onChange={(value) => updateForm("revenue", Number(value || 0))} />
          <SelectField
            label="Status"
            value={form.status}
            options={projectStatusOptions}
            onChange={(value) => updateForm("status", value as (typeof projectStatusOptions)[number])}
          />
          <TextField label="Assigned Team" value={form.assignedTeam} onChange={(value) => updateForm("assignedTeam", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Notes" value={form.notes} onChange={(value) => updateForm("notes", value)} />
          <div className="flex justify-end md:col-span-2 xl:col-span-3">
            <button type="submit" className="admin-primary-button focus-ring">
              <Plus className="size-4" aria-hidden="true" />
              Save Project
            </button>
          </div>
        </form>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Search and Filter" subtitle="Filter consulting projects by phase, status, or keyword." />
        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_220px_220px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search client, team, notes, or phase"
              className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold"
            />
          </label>
          <SelectField
            label="Phase"
            value={phaseFilter}
            options={["All", ...projectPhaseOptions]}
            onChange={(value) => setPhaseFilter(value as "All" | (typeof projectPhaseOptions)[number])}
          />
          <SelectField
            label="Status"
            value={statusFilter}
            options={["All", ...projectStatusOptions]}
            onChange={(value) => setStatusFilter(value as "All" | (typeof projectStatusOptions)[number])}
          />
        </div>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Consulting Projects" subtitle="Open a project record to update delivery details and timeline activity." />
        <div className="mt-5 grid gap-4">
          {filteredProjects.length ? (
            filteredProjects.map((record) => <ProjectCard key={record.id} record={record} />)
          ) : (
            <EmptyState
              title="No projects match the current filter"
              description="Your first Truevance project will appear here once a record is created."
            />
          )}
        </div>
      </section>
    </WorkspaceShell>
  );
}

export function TruevanceProjectDetailPage() {
  const { projectId } = useParams();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();

  useEffect(() => {
    document.title = `Project Detail | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const record = useMemo(
    () => (store && projectId ? store.projects.find((item) => item.id === projectId) || null : null),
    [projectId, store],
  );

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Project Detail"
        description="We could not load the selected consulting project record."
        detail={loadError || "The record could not be loaded from the Truevance store."}
      />
    );
  }
  if (loadStatus === "loading" || !store) {
    return <LoadingShell title="Project Detail" description="Loading consulting project record." />;
  }

  if (!record) {
    return (
      <NotFoundShell
        title="Project not found"
        description="The requested consulting project does not exist in the current Truevance store."
        backHref="/admin/truevance-projects"
        backLabel="Back to Projects"
      />
    );
  }

  const currentRecord = record;

  async function persist(nextRecord: ConsultingProjectRecord) {
    const response = await updateTruevanceProject(nextRecord.id, nextRecord);
    if (!response?.project) return;
    setStore((current) =>
      current
        ? {
            ...current,
            projects: current.projects.map((item) => (item.id === response.project.id ? response.project : item)),
          }
        : current,
    );
  }

  function updateField<K extends keyof ConsultingProjectRecord>(key: K, value: ConsultingProjectRecord[K]) {
    if (!currentRecord) return;
    void persist({ ...currentRecord, [key]: value, updatedAt: new Date().toISOString() });
  }

  function addEvent(event: FormEvent<HTMLFormElement>, detailForm: { title: string; detail: string }) {
    event.preventDefault();
    if (!detailForm.title.trim()) return;
    void appendTruevanceProjectEvent(currentRecord.id, { title: detailForm.title, detail: detailForm.detail }).then((response) => {
      if (!response?.project) return;
      setStore((current) =>
        current
          ? {
              ...current,
              projects: current.projects.map((item) => (item.id === response.project.id ? response.project : item)),
            }
          : current,
      );
    });
  }

  return (
    <RecordDetailShell
      title="Project Detail"
      eyebrow={TRUEVANCE_BRAND_NAME}
      description="Keep the consulting delivery record current with phase updates, completion percentage, and timeline notes."
      backHref="/admin/truevance-projects"
      backLabel="Back to Projects"
      recordLabel={record.projectId}
    >
      <div className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Project Information" subtitle="Update the consulting delivery record." />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <TextField label="Client" value={record.client} onChange={(value) => updateField("client", value)} />
            <TextField label="Start Date" type="date" value={record.startDate} onChange={(value) => updateField("startDate", value)} />
            <SelectField
              label="Current Phase"
              value={record.currentPhase}
              options={projectPhaseOptions}
              onChange={(value) => updateField("currentPhase", value as ConsultingProjectRecord["currentPhase"])}
            />
            <TextField label="Completion %" type="number" value={String(record.completionPercent)} onChange={(value) => updateField("completionPercent", Number(value || 0))} />
            <TextField label="Revenue" type="number" value={String(record.revenue)} onChange={(value) => updateField("revenue", Number(value || 0))} />
            <SelectField
              label="Status"
              value={record.status}
              options={projectStatusOptions}
              onChange={(value) => updateField("status", value as ConsultingProjectRecord["status"])}
            />
            <TextField label="Assigned Team" value={record.assignedTeam} onChange={(value) => updateField("assignedTeam", value)} />
            <TextAreaField className="md:col-span-2" label="Notes" value={record.notes} onChange={(value) => updateField("notes", value)} />
          </div>
        </article>

        <TimelinePanel
          title="Project Timeline"
          subtitle="Log delivery milestones, implementation updates, and completion notes."
          events={record.timelineEvents}
          onAdd={addEvent}
        />
      </div>
    </RecordDetailShell>
  );
}

function WorkspaceShell({
  title,
  eyebrow,
  description,
  icon: Icon,
  metrics,
  actions,
  children,
}: {
  title: string;
  eyebrow: string;
  description: string;
  icon: typeof Target;
  metrics: Array<{ label: string; value: string | number; note: string }>;
  actions: ReactNode;
  children: ReactNode;
}) {
  useTruevanceBranding(title, description);

  return (
    <main className="truevance-shell command-center-shell min-h-screen text-[#fff9ef]">
      <TruevanceBrandHeader />
      <section className="truevance-hero relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,224,208,.2),transparent_34%),radial-gradient(circle_at_top_right,rgba(214,178,74,.18),transparent_30%),linear-gradient(120deg,rgba(7,26,47,.96),rgba(10,37,64,.92))]" />
        <div className="relative mx-auto grid max-w-7xl gap-5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[rgba(34,224,208,.32)] px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#22E0D0]">
            <Icon className="size-4" aria-hidden="true" />
            {eyebrow}
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{title}</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#E8EEF4]/82">{description}</p>
          {actions}
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {metrics.map((metric) => (
              <article key={metric.label} className="command-center-card rounded-lg p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{metric.label}</p>
                <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{metric.value}</p>
                <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">{metric.note}</p>
              </article>
            ))}
          </section>

          {children}
        </div>
      </section>
    </main>
  );
}

function RecordDetailShell({
  title,
  eyebrow,
  description,
  backHref,
  backLabel,
  recordLabel,
  children,
}: {
  title: string;
  eyebrow: string;
  description: string;
  backHref: string;
  backLabel: string;
  recordLabel: string;
  children: ReactNode;
}) {
  useTruevanceBranding(title, description);
  return (
    <main className="truevance-shell command-center-shell min-h-screen text-[#fff9ef]">
      <TruevanceBrandHeader />
      <section className="truevance-hero relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,224,208,.2),transparent_34%),radial-gradient(circle_at_top_right,rgba(214,178,74,.18),transparent_30%),linear-gradient(120deg,rgba(7,26,47,.96),rgba(10,37,64,.92))]" />
        <div className="relative mx-auto grid max-w-7xl gap-5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[rgba(34,224,208,.32)] px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#22E0D0]">
            {eyebrow}
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{title}</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#E8EEF4]/82">{description}</p>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#22E0D0]">Record ID: {recordLabel}</p>
          <Link to={backHref} className="focus-ring inline-flex w-fit items-center gap-2 rounded-lg text-sm font-bold text-[#22E0D0]">
            <ArrowLeft className="size-4" aria-hidden="true" />
            {backLabel}
          </Link>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">{children}</div>
      </section>
    </main>
  );
}

function OpportunityCard({ record }: { record: ConsultingOpportunityRecord }) {
  return (
    <article className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)] lg:grid-cols-[1.2fr_.8fr_.7fr_.7fr_auto] lg:items-center">
      <div>
        <h3 className="font-display text-2xl font-bold text-[#050505]">{record.companyName}</h3>
        <p className="mt-1 text-sm font-semibold text-[#5c5246]">{record.contactName || "Contact not set"}</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">
          {record.industry || "Industry not set"} · {record.opportunityType || "Opportunity type not set"}
        </p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{record.currentStage}</p>
        <p className="mt-1 text-[#5c5246]">{record.status}</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{formatCurrency(record.estimatedRevenue)}</p>
        <p className="mt-1 text-[#5c5246]">{formatPercent(record.probability)}</p>
      </div>
      <div className="text-sm font-bold text-[#5c5246]">
        <p>Follow-up</p>
        <p className="mt-1 text-[#332a1f]">{formatDisplayDate(record.followUpDate)}</p>
      </div>
      <Link
        to={`/admin/truevance-opportunities/${record.id}`}
        className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]"
      >
        View Record
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function AssessmentCard({ record }: { record: BusinessAssessmentRecord }) {
  return (
    <article className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)] lg:grid-cols-[1.15fr_.85fr_.7fr_.7fr_auto] lg:items-center">
      <div>
        <h3 className="font-display text-2xl font-bold text-[#050505]">{record.client}</h3>
        <p className="mt-1 text-sm font-semibold text-[#5c5246]">{record.assessmentId}</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{record.recommendations || "No recommendations yet."}</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{record.status}</p>
        <p className="mt-1 text-[#5c5246]">{record.priorityLevel} priority</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{formatDisplayDate(record.assessmentDate)}</p>
        <p className="mt-1 text-[#5c5246]">{record.assignedConsultant}</p>
      </div>
      <div className="text-sm font-bold text-[#5c5246]">
        <p>Follow-up</p>
        <p className="mt-1 text-[#332a1f]">{record.followUpActions || "None set"}</p>
      </div>
      <Link
        to={`/admin/truevance-assessments/${record.id}`}
        className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]"
      >
        View Record
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function ProjectCard({ record }: { record: ConsultingProjectRecord }) {
  return (
    <article className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)] lg:grid-cols-[1.15fr_.85fr_.7fr_.7fr_auto] lg:items-center">
      <div>
        <h3 className="font-display text-2xl font-bold text-[#050505]">{record.client}</h3>
        <p className="mt-1 text-sm font-semibold text-[#5c5246]">{record.projectId}</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{record.assignedTeam || "Team not assigned"}</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{record.currentPhase}</p>
        <p className="mt-1 text-[#5c5246]">{record.status}</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{formatCurrency(record.revenue)}</p>
        <p className="mt-1 text-[#5c5246]">{formatPercent(record.completionPercent)}</p>
      </div>
      <div className="text-sm font-bold text-[#5c5246]">
        <p>Start date</p>
        <p className="mt-1 text-[#332a1f]">{formatDisplayDate(record.startDate)}</p>
      </div>
      <Link
        to={`/admin/truevance-projects/${record.id}`}
        className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]"
      >
        View Record
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function TimelinePanel({
  title,
  subtitle,
  events,
  onAdd,
}: {
  title: string;
  subtitle: string;
  events: Array<{ id: string; date: string; title: string; detail: string }>;
  onAdd: (event: FormEvent<HTMLFormElement>, detailForm: { title: string; detail: string }) => void;
}) {
  const [detailForm, setDetailForm] = useState({ title: "", detail: "" });
  return (
    <article className="command-center-card rounded-lg p-5 sm:p-6">
      <SectionHeading title={title} subtitle={subtitle} />
      <form onSubmit={(event) => onAdd(event, detailForm)} className="mt-5 grid gap-4">
        <TextField label="Timeline Event Title" required value={detailForm.title} onChange={(value) => setDetailForm((current) => ({ ...current, title: value }))} />
        <TextAreaField label="Timeline Event Detail" value={detailForm.detail} onChange={(value) => setDetailForm((current) => ({ ...current, detail: value }))} />
        <div className="flex justify-end">
          <button type="submit" className="admin-primary-button focus-ring">
            <Plus className="size-4" aria-hidden="true" />
            Add Update
          </button>
        </div>
      </form>
      <div className="mt-6 grid gap-3">
        {events.length ? (
          events.map((event) => (
            <article key={event.id} className="rounded-lg border border-[#FF6A2B]/18 bg-white/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{event.title}</p>
                <p className="text-xs font-bold text-[#fff9ef]/64">{new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(event.date))}</p>
              </div>
              <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/74">{event.detail}</p>
            </article>
          ))
        ) : (
          <EmptyState title="No timeline activity yet" description="Add the first timeline note to keep this record current." />
        )}
      </div>
    </article>
  );
}

function LoadingShell({ title, description }: { title: string; description: string }) {
  useTruevanceBranding(title, description);
  return (
    <main className="truevance-shell command-center-shell min-h-screen text-[#fff9ef]">
      <TruevanceBrandHeader />
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl rounded-2xl border border-[rgba(34,224,208,.18)] bg-[#F5F7FA] p-8 text-center shadow-[0_24px_60px_rgba(7,26,47,.18)]">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#17D4C4]">{title}</p>
          <h1 className="mt-3 font-display text-4xl font-bold text-[#071A2F]">{description}</h1>
          <p className="mt-4 text-sm font-semibold leading-7 text-[#334155]">Loading the consulting workspace and its saved records.</p>
        </div>
      </section>
    </main>
  );
}

function WorkspaceErrorShell({
  title,
  description,
  detail,
}: {
  title: string;
  description: string;
  detail: string;
}) {
  useTruevanceBranding(title, description);
  return (
    <main className="truevance-shell command-center-shell min-h-screen text-[#fff9ef]">
      <TruevanceBrandHeader />
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl rounded-2xl border border-[rgba(34,224,208,.18)] bg-[#F5F7FA] p-8 text-center shadow-[0_24px_60px_rgba(7,26,47,.18)]">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#17D4C4]">{title}</p>
          <h1 className="mt-3 font-display text-4xl font-bold text-[#071A2F]">Workspace unavailable</h1>
          <p className="mt-4 text-sm font-semibold leading-7 text-[#334155]">{description}</p>
          <p className="mt-4 rounded-lg border border-[rgba(34,224,208,.16)] bg-white px-4 py-3 text-sm font-semibold leading-7 text-[#334155]">
            {detail}
          </p>
          <button type="button" onClick={() => window.location.reload()} className="admin-primary-button focus-ring mx-auto mt-6">
            Retry loading
          </button>
        </div>
      </section>
    </main>
  );
}

function NotFoundShell({
  title,
  description,
  backHref,
  backLabel,
}: {
  title: string;
  description: string;
  backHref: string;
  backLabel: string;
}) {
  useTruevanceBranding(title, description);
  return (
    <main className="truevance-shell command-center-shell min-h-screen text-[#fff9ef]">
      <TruevanceBrandHeader />
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl rounded-2xl border border-[rgba(34,224,208,.18)] bg-[#F5F7FA] p-8 text-center shadow-[0_24px_60px_rgba(7,26,47,.18)]">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#17D4C4]">{TRUEVANCE_BRAND_NAME}</p>
          <h1 className="mt-3 font-display text-4xl font-bold text-[#071A2F]">{title}</h1>
          <p className="mt-4 text-sm font-semibold leading-7 text-[#334155]">{description}</p>
          <Link to={backHref} className="admin-primary-button focus-ring mx-auto mt-6">
            {backLabel}
          </Link>
        </div>
      </section>
    </main>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(34,224,208,.18)] bg-[rgba(245,247,250,.95)] p-6 text-center text-[#071A2F] shadow-[0_18px_45px_rgba(7,26,47,.12)]">
      <p className="font-display text-2xl font-bold text-[#071A2F]">{title}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#334155]">{description}</p>
    </div>
  );
}

function SectionHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="border-b border-[#FF6A2B]/14 pb-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#17D4C4]">{TRUEVANCE_BRAND_NAME}</p>
      <h2 className="mt-1 font-display text-3xl font-bold text-[#fff9ef]">{title}</h2>
      <p className="mt-3 max-w-4xl text-sm font-semibold leading-7 text-[#E8EEF4]/72">{subtitle}</p>
    </div>
  );
}

function TextField({
  className = "",
  label,
  onChange,
  required,
  type = "text",
  value,
}: {
  className?: string;
  label: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  value: string;
}) {
  return (
    <label className={`grid gap-2 text-sm font-bold text-[#fff9ef] ${className}`}>
      <span>{label}</span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="command-center-field focus-ring min-h-11 rounded-lg px-4 text-sm font-semibold"
      />
    </label>
  );
}

function TextAreaField({
  className = "",
  label,
  onChange,
  value,
}: {
  className?: string;
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className={`grid gap-2 text-sm font-bold text-[#fff9ef] ${className}`}>
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} className="command-center-field focus-ring min-h-28 rounded-lg px-4 py-3 text-sm font-semibold" />
    </label>
  );
}

function SelectField({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  options: readonly string[];
  value: string;
}) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className="command-center-field focus-ring min-h-11 rounded-lg px-4 text-sm font-bold">
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function createBlankOpportunityForm() {
  return {
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    industry: "",
    leadSource: "Referral",
    opportunityType: "Business Consulting",
    estimatedRevenue: 0,
    probability: 50,
    currentStage: "New Inquiry" as (typeof consultingOpportunityStageOptions)[number],
    assignedConsultant: TRUEVANCE_BRAND_NAME,
    nextAction: "",
    followUpDate: "",
    notes: "",
    status: "Active" as (typeof consultingOpportunityStatusOptions)[number],
  };
}

function createBlankAssessmentForm() {
  return {
    client: "",
    assessmentDate: "",
    businessChallenges: "",
    operationalChallenges: "",
    customerExperienceChallenges: "",
    technologyChallenges: "",
    recommendations: "",
    priorityLevel: "Medium" as (typeof assessmentPriorityOptions)[number],
    estimatedImpact: "",
    assignedConsultant: TRUEVANCE_BRAND_NAME,
    followUpActions: "",
    status: "Planned" as (typeof assessmentStatusOptions)[number],
  };
}

function createBlankProjectForm() {
  return {
    client: "",
    startDate: "",
    currentPhase: "Discovery" as (typeof projectPhaseOptions)[number],
    completionPercent: 0,
    revenue: 0,
    assignedTeam: TRUEVANCE_BRAND_NAME,
    notes: "",
    status: "Active" as (typeof projectStatusOptions)[number],
  };
}

function createBlankConsultingAssessmentForm() {
  return {
    client: "",
    assessmentName: "",
    assessmentCategory: "Business Operations" as (typeof assessmentCategoryOptions)[number],
    assessmentQuestions: "What are the biggest operational blockers?\nWhat customer experience gaps are most visible?\nWhat should be automated first?",
    assessmentScores: "Business Operations: 72\nCustomer Experience: 68\nAutomation Readiness: 78",
    assessmentDate: "",
    businessChallenges: "",
    operationalChallenges: "",
    customerExperienceChallenges: "",
    technologyChallenges: "",
    recommendations: "",
    priorityLevel: "High" as (typeof assessmentPriorityOptions)[number],
    estimatedImpact: "",
    assignedConsultant: TRUEVANCE_BRAND_NAME,
    followUpActions: "",
    status: "Planned" as (typeof assessmentStatusOptions)[number],
  };
}

function createBlankRecommendationForm() {
  return {
    assessmentId: "",
    assessmentName: "",
    client: "",
    opportunityIdentified: "",
    impactLevel: "High",
    priorityLevel: "High" as (typeof assessmentPriorityOptions)[number],
    estimatedEffort: "Medium",
    suggestedSolution: "",
    serviceCategoryAlignment: "Digital Solutions",
    notes: "",
    status: "Draft" as (typeof recommendationStatusOptions)[number],
  };
}

function createBlankRoadmapForm() {
  return {
    roadmapHorizon: "Quick Wins" as (typeof roadmapHorizonOptions)[number],
    client: "",
    title: "",
    priorityLevel: "High" as (typeof assessmentPriorityOptions)[number],
    owner: TRUEVANCE_BRAND_NAME,
    dueDate: "",
    notes: "",
    linkedAssessmentId: "",
    linkedAssessmentName: "",
    linkedOpportunityId: "",
    linkedOpportunityName: "",
    status: "Planned" as (typeof roadmapStatusOptions)[number],
  };
}

export function ConsultingAssessmentEnginePage() {
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | (typeof assessmentStatusOptions)[number]>("All");
  const [categoryFilter, setCategoryFilter] = useState<"All" | (typeof assessmentCategoryOptions)[number]>("All");
  const [form, setForm] = useState(createBlankConsultingAssessmentForm());

  useEffect(() => {
    document.title = `Assessment Center | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const metrics = useMemo(() => (store ? calculateTruevanceMetrics(store) : null), [store]);
  const assessments = useMemo(() => {
    if (!store) return [];
    const normalizedQuery = query.trim().toLowerCase();
    return store.assessments.filter((record) => {
      if (!record.assessmentId.startsWith("TV-CAS")) return false;
      const matchesStatus = statusFilter === "All" || record.status === statusFilter;
      const matchesCategory = categoryFilter === "All" || record.assessmentCategory === categoryFilter;
      const haystack = flattenRecordText(
        record.assessmentId,
        record.client,
        record.assessmentName,
        record.assessmentCategory,
        record.assignedConsultant,
        record.businessChallenges,
        record.operationalChallenges,
        record.customerExperienceChallenges,
        record.technologyChallenges,
        record.recommendations,
      );
      return matchesStatus && matchesCategory && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [categoryFilter, query, statusFilter, store]);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleCreateAssessment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const created = await createTruevanceAssessment({
      client: form.client,
      assessmentName: form.assessmentName || form.client,
      assessmentCategory: form.assessmentCategory,
      assessmentQuestions: parseAssessmentQuestions(form.assessmentQuestions),
      assessmentScores: parseAssessmentScores(form.assessmentScores),
      assessmentDate: form.assessmentDate,
      businessChallenges: form.businessChallenges,
      operationalChallenges: form.operationalChallenges,
      customerExperienceChallenges: form.customerExperienceChallenges,
      technologyChallenges: form.technologyChallenges,
      recommendations: form.recommendations,
      priorityLevel: form.priorityLevel,
      estimatedImpact: form.estimatedImpact,
      assignedConsultant: form.assignedConsultant,
      followUpActions: form.followUpActions,
      status: form.status,
      assessmentType: "Consulting Assessment",
    });
    if (!created?.assessment) return;
    setStore((current) =>
      current
        ? {
            ...current,
            assessments: [created.assessment, ...current.assessments],
          }
        : { opportunities: [], assessments: [created.assessment], recommendations: [], roadmaps: [], projects: [] },
    );
    setForm(createBlankConsultingAssessmentForm());
    navigate(`/admin/consulting-assessments/${created.assessment.id}`);
  }

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Assessment Center"
        description="We could not load the consulting assessment workspace from the production data source."
        detail={loadError || "The workspace fetch failed before the assessment records could load."}
      />
    );
  }
  if (loadStatus === "loading" || !store || !metrics) return <LoadingShell title="Assessment Center" description="Loading consulting assessment records." />;

  return (
    <WorkspaceShell
      title="Assessment Center"
      eyebrow="Phase 2D"
      description="Structure consulting assessments, track scores, capture recommendations, and hand off to the next phase without re-entering data."
      icon={Search}
      metrics={[
        { label: "Active Assessments", value: metrics.activeAssessments, note: "Structured assessments in motion." },
        { label: "Recommendations", value: metrics.activeRecommendations, note: "Linked recommendations prepared from assessments." },
        { label: "Pipeline Value", value: formatCurrency(metrics.pipelineValue), note: "Weighted consulting opportunity value." },
        { label: "Implementation Progress", value: formatPercent(metrics.implementationProgress), note: "Progress across roadmap records." },
      ]}
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/truevance-connect" className="admin-secondary-button focus-ring">
            Back to Business Center
          </Link>
          <Link to="/admin/truevance-recommendations" className="admin-secondary-button focus-ring">
            Recommendations
          </Link>
          <Link to="/admin/implementation-roadmaps" className="admin-secondary-button focus-ring">
            Roadmaps
          </Link>
        </div>
      }
    >
      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Create Consulting Assessment" subtitle="Capture the consulting context, category, questions, scores, and recommendations in one record." />
        <form onSubmit={handleCreateAssessment} className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <TextField label="Client" required value={form.client} onChange={(value) => updateForm("client", value)} />
          <TextField label="Assessment Name" value={form.assessmentName} onChange={(value) => updateForm("assessmentName", value)} />
          <SelectField label="Assessment Category" value={form.assessmentCategory} options={assessmentCategoryOptions} onChange={(value) => updateForm("assessmentCategory", value as (typeof assessmentCategoryOptions)[number])} />
          <TextField label="Assessment Date" type="date" value={form.assessmentDate} onChange={(value) => updateForm("assessmentDate", value)} />
          <TextField label="Assigned Consultant" value={form.assignedConsultant} onChange={(value) => updateForm("assignedConsultant", value)} />
          <SelectField label="Priority" value={form.priorityLevel} options={assessmentPriorityOptions} onChange={(value) => updateForm("priorityLevel", value as (typeof assessmentPriorityOptions)[number])} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Assessment Questions" value={form.assessmentQuestions} onChange={(value) => updateForm("assessmentQuestions", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Assessment Scores" value={form.assessmentScores} onChange={(value) => updateForm("assessmentScores", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Business Challenges" value={form.businessChallenges} onChange={(value) => updateForm("businessChallenges", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Operational Challenges" value={form.operationalChallenges} onChange={(value) => updateForm("operationalChallenges", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Customer Experience Challenges" value={form.customerExperienceChallenges} onChange={(value) => updateForm("customerExperienceChallenges", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Technology Challenges" value={form.technologyChallenges} onChange={(value) => updateForm("technologyChallenges", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Recommendations" value={form.recommendations} onChange={(value) => updateForm("recommendations", value)} />
          <TextField label="Estimated Impact" value={form.estimatedImpact} onChange={(value) => updateForm("estimatedImpact", value)} />
          <TextField label="Follow-Up Actions" value={form.followUpActions} onChange={(value) => updateForm("followUpActions", value)} />
          <SelectField label="Status" value={form.status} options={assessmentStatusOptions} onChange={(value) => updateForm("status", value as (typeof assessmentStatusOptions)[number])} />
          <div className="flex justify-end md:col-span-2 xl:col-span-3">
            <button type="submit" className="admin-primary-button focus-ring">
              <Plus className="size-4" aria-hidden="true" />
              Save Assessment
            </button>
          </div>
        </form>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1fr_.85fr]">
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Search and Filter" subtitle="Narrow the consulting assessment list by category, status, or keyword." />
          <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_220px_220px]">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search client, questions, or notes" className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold" />
            </label>
            <SelectField label="Category" value={categoryFilter} options={["All", ...assessmentCategoryOptions]} onChange={(value) => setCategoryFilter(value as "All" | (typeof assessmentCategoryOptions)[number])} />
            <SelectField label="Status" value={statusFilter} options={["All", ...assessmentStatusOptions]} onChange={(value) => setStatusFilter(value as "All" | (typeof assessmentStatusOptions)[number])} />
          </div>
        </article>
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Assessment Flow" subtitle="A structured assessment should flow directly into a recommendation and then into an opportunity." />
          <div className="mt-5 grid gap-3">
            {[
              { label: "Assessment", to: "/admin/consulting-assessments" },
              { label: "Recommendation", to: "/admin/truevance-recommendations" },
              { label: "Opportunity", to: "/admin/truevance-opportunities" },
              { label: "Project", to: "/admin/truevance-projects" },
            ].map((item) => (
              <Link key={item.label} to={item.to} className="focus-ring flex items-center justify-between rounded-lg border border-[#FF6A2B]/18 bg-white/5 px-4 py-3 text-sm font-bold text-[#fff9ef] transition hover:-translate-y-0.5 hover:border-[#FF6A2B]/55 hover:bg-white/10">
                {item.label}
                <ArrowRight className="size-4 text-[#FF6A2B]" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </article>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Consulting Assessments" subtitle="Open a record to review the structured framework, update the assessment, and hand off to recommendations." />
        <div className="mt-5 grid gap-4">
          {assessments.length ? assessments.map((record) => <ConsultingAssessmentCard key={record.id} record={record} />) : <EmptyState title="No consulting assessments yet" description="Your first consulting assessment will appear here once it is saved." />}
        </div>
      </section>
    </WorkspaceShell>
  );
}

export function ConsultingAssessmentDetailPage() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();

  useEffect(() => {
    document.title = `Consulting Assessment Detail | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const record = useMemo(
    () => (store && assessmentId ? store.assessments.find((item) => item.id === assessmentId && item.assessmentId.startsWith("TV-CAS")) || null : null),
    [assessmentId, store],
  );

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Consulting Assessment Detail"
        description="We could not load the selected consulting assessment record."
        detail={loadError || "The record could not be loaded from the Truevance store."}
      />
    );
  }
  if (loadStatus === "loading" || !store) return <LoadingShell title="Consulting Assessment Detail" description="Loading consulting assessment record." />;
  if (!record) {
    return <NotFoundShell title="Assessment not found" description="The requested consulting assessment does not exist in the current Truevance store." backHref="/admin/consulting-assessments" backLabel="Back to Assessments" />;
  }
  const assessmentRecord = record;

  async function persist(nextRecord: BusinessAssessmentRecord) {
    const response = await updateTruevanceAssessment(nextRecord.id, nextRecord);
    if (!response?.assessment) return;
    setStore((current) =>
      current
        ? { ...current, assessments: current.assessments.map((item) => (item.id === response.assessment.id ? response.assessment : item)) }
        : current,
    );
  }

  function updateField<K extends keyof BusinessAssessmentRecord>(key: K, value: BusinessAssessmentRecord[K]) {
    void persist({ ...assessmentRecord, [key]: value, updatedAt: new Date().toISOString() });
  }

  function addEvent(event: FormEvent<HTMLFormElement>, detailForm: { title: string; detail: string }) {
    event.preventDefault();
    if (!detailForm.title.trim()) return;
    void appendTruevanceAssessmentEvent(assessmentRecord.id, { title: detailForm.title, detail: detailForm.detail }).then((response) => {
      if (!response?.assessment) return;
      setStore((current) =>
        current
          ? { ...current, assessments: current.assessments.map((item) => (item.id === response.assessment.id ? response.assessment : item)) }
          : current,
      );
    });
  }

  async function createRecommendationFromAssessment() {
    const created = await createTruevanceRecommendation({
      assessmentId: assessmentRecord.assessmentId,
      assessmentName: assessmentRecord.assessmentName,
      client: assessmentRecord.client,
      opportunityIdentified: assessmentRecord.recommendations || assessmentRecord.estimatedImpact || "Recommendation from consulting assessment",
      impactLevel: "High",
      priorityLevel: assessmentRecord.priorityLevel,
      estimatedEffort: "Medium",
      suggestedSolution: assessmentRecord.recommendations || "Implement the top assessment recommendations.",
      serviceCategoryAlignment: assessmentRecord.assessmentCategory,
      notes: assessmentRecord.followUpActions || "Created from consulting assessment.",
      status: "Draft",
    });
    if (!created?.recommendation) return;
    setStore((current) =>
      current
        ? { ...current, recommendations: [created.recommendation, ...current.recommendations] }
        : { opportunities: [], assessments: [], recommendations: [created.recommendation], roadmaps: [], projects: [] },
    );
    navigate(`/admin/truevance-recommendations/${created.recommendation.id}`);
  }

  return (
    <RecordDetailShell
      title="Consulting Assessment Detail"
      eyebrow="Phase 2D"
      description="Review the consulting assessment, keep the framework current, and create a linked recommendation when ready."
      backHref="/admin/consulting-assessments"
      backLabel="Back to Assessments"
      recordLabel={assessmentRecord.assessmentId}
    >
      <div className="mb-6 flex flex-wrap gap-2">
        <button onClick={() => void createRecommendationFromAssessment()} className="admin-primary-button focus-ring">
          <Sparkles className="size-4" aria-hidden="true" />
          Create Recommendation
        </button>
        <Link to="/admin/truevance-opportunities" className="admin-secondary-button focus-ring">
          View Opportunities
        </Link>
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Assessment Information" subtitle="Update the structured consulting assessment." />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <TextField label="Client" value={assessmentRecord.client} onChange={(value) => updateField("client", value)} />
            <TextField label="Assessment Name" value={assessmentRecord.assessmentName} onChange={(value) => updateField("assessmentName", value)} />
            <SelectField label="Category" value={assessmentRecord.assessmentCategory} options={assessmentCategoryOptions} onChange={(value) => updateField("assessmentCategory", value as BusinessAssessmentRecord["assessmentCategory"]) } />
            <TextField label="Assessment Date" type="date" value={assessmentRecord.assessmentDate} onChange={(value) => updateField("assessmentDate", value)} />
            <TextField label="Assigned Consultant" value={assessmentRecord.assignedConsultant} onChange={(value) => updateField("assignedConsultant", value)} />
            <SelectField label="Priority" value={assessmentRecord.priorityLevel} options={assessmentPriorityOptions} onChange={(value) => updateField("priorityLevel", value as BusinessAssessmentRecord["priorityLevel"]) } />
            <TextAreaField className="md:col-span-2" label="Assessment Questions" value={formatAssessmentQuestions(assessmentRecord.assessmentQuestions)} onChange={(value) => updateField("assessmentQuestions", parseAssessmentQuestions(value))} />
            <TextAreaField className="md:col-span-2" label="Assessment Scores" value={formatAssessmentScores(assessmentRecord.assessmentScores)} onChange={(value) => updateField("assessmentScores", parseAssessmentScores(value))} />
            <TextAreaField className="md:col-span-2" label="Business Challenges" value={assessmentRecord.businessChallenges} onChange={(value) => updateField("businessChallenges", value)} />
            <TextAreaField className="md:col-span-2" label="Operational Challenges" value={assessmentRecord.operationalChallenges} onChange={(value) => updateField("operationalChallenges", value)} />
            <TextAreaField className="md:col-span-2" label="Customer Experience Challenges" value={assessmentRecord.customerExperienceChallenges} onChange={(value) => updateField("customerExperienceChallenges", value)} />
            <TextAreaField className="md:col-span-2" label="Technology Challenges" value={assessmentRecord.technologyChallenges} onChange={(value) => updateField("technologyChallenges", value)} />
            <TextAreaField className="md:col-span-2" label="Recommendations" value={assessmentRecord.recommendations} onChange={(value) => updateField("recommendations", value)} />
            <TextField label="Estimated Impact" value={assessmentRecord.estimatedImpact} onChange={(value) => updateField("estimatedImpact", value)} />
            <TextField label="Follow-Up Actions" value={assessmentRecord.followUpActions} onChange={(value) => updateField("followUpActions", value)} />
            <SelectField label="Status" value={assessmentRecord.status} options={assessmentStatusOptions} onChange={(value) => updateField("status", value as BusinessAssessmentRecord["status"]) } />
          </div>
        </article>
        <TimelinePanel title="Assessment Timeline" subtitle="Log assessment milestones and hand-off notes." events={record.timelineEvents} onAdd={addEvent} />
      </div>
    </RecordDetailShell>
  );
}

export function ConsultingRecommendationEnginePage() {
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | (typeof recommendationStatusOptions)[number]>("All");
  const [form, setForm] = useState(createBlankRecommendationForm());

  useEffect(() => {
    document.title = `Consulting Recommendations | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const recommendations = useMemo(() => {
    if (!store) return [];
    const normalizedQuery = query.trim().toLowerCase();
    return store.recommendations.filter((record) => {
      const matchesStatus = statusFilter === "All" || record.status === statusFilter;
      const haystack = flattenRecordText(record.recommendationId, record.assessmentName, record.client, record.opportunityIdentified, record.suggestedSolution, record.serviceCategoryAlignment, record.notes);
      return matchesStatus && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [query, statusFilter, store]);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleCreateRecommendation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const created = await createTruevanceRecommendation({
      assessmentId: form.assessmentId,
      assessmentName: form.assessmentName,
      client: form.client,
      opportunityIdentified: form.opportunityIdentified,
      impactLevel: form.impactLevel,
      priorityLevel: form.priorityLevel,
      estimatedEffort: form.estimatedEffort,
      suggestedSolution: form.suggestedSolution,
      serviceCategoryAlignment: form.serviceCategoryAlignment,
      notes: form.notes,
      status: form.status,
    });
    if (!created?.recommendation) return;
    setStore((current) =>
      current
        ? { ...current, recommendations: [created.recommendation, ...current.recommendations] }
        : { opportunities: [], assessments: [], recommendations: [created.recommendation], roadmaps: [], projects: [] },
    );
    setForm(createBlankRecommendationForm());
    navigate(`/admin/truevance-recommendations/${created.recommendation.id}`);
  }

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Consulting Recommendations"
        description="We could not load the consulting recommendation workspace from the production data source."
        detail={loadError || "The workspace fetch failed before the recommendation records could load."}
      />
    );
  }
  if (loadStatus === "loading" || !store) return <LoadingShell title="Consulting Recommendations" description="Loading consulting recommendation records." />;

  return (
    <WorkspaceShell
      title="Consulting Recommendation Engine"
      eyebrow="Phase 2D"
      description="Turn assessments into concrete opportunities and handoffs without re-entering the client context."
      icon={Sparkles}
      metrics={[
        { label: "Recommendations", value: store.recommendations.length, note: "Structured recommendations ready or in motion." },
        { label: "Active Assessments", value: calculateTruevanceMetrics(store).activeAssessments, note: "Assessment work still in progress." },
        { label: "Open Opportunities", value: calculateTruevanceMetrics(store).openOpportunities, note: "Active consulting opportunities." },
        { label: "Pipeline Value", value: formatCurrency(calculateTruevanceMetrics(store).pipelineValue), note: "Weighted consulting opportunity value." },
      ]}
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/consulting-assessments" className="admin-secondary-button focus-ring">
            Assessments
          </Link>
          <Link to="/admin/implementation-roadmaps" className="admin-secondary-button focus-ring">
            Roadmaps
          </Link>
          <Link to="/admin/truevance-opportunities" className="admin-secondary-button focus-ring">
            Opportunities
          </Link>
        </div>
      }
    >
      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Create Recommendation" subtitle="Link a recommendation to an assessment and specify the solution direction." />
        <form onSubmit={handleCreateRecommendation} className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <TextField label="Assessment ID" value={form.assessmentId} onChange={(value) => updateForm("assessmentId", value)} />
          <TextField label="Assessment Name" value={form.assessmentName} onChange={(value) => updateForm("assessmentName", value)} />
          <TextField label="Client" required value={form.client} onChange={(value) => updateForm("client", value)} />
          <TextField label="Opportunity Identified" value={form.opportunityIdentified} onChange={(value) => updateForm("opportunityIdentified", value)} />
          <TextField label="Impact Level" value={form.impactLevel} onChange={(value) => updateForm("impactLevel", value)} />
          <SelectField label="Priority" value={form.priorityLevel} options={assessmentPriorityOptions} onChange={(value) => updateForm("priorityLevel", value as (typeof assessmentPriorityOptions)[number])} />
          <TextField label="Estimated Effort" value={form.estimatedEffort} onChange={(value) => updateForm("estimatedEffort", value)} />
          <TextField label="Service Category Alignment" value={form.serviceCategoryAlignment} onChange={(value) => updateForm("serviceCategoryAlignment", value)} />
          <SelectField label="Status" value={form.status} options={recommendationStatusOptions} onChange={(value) => updateForm("status", value as (typeof recommendationStatusOptions)[number])} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Suggested Solution" value={form.suggestedSolution} onChange={(value) => updateForm("suggestedSolution", value)} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Notes" value={form.notes} onChange={(value) => updateForm("notes", value)} />
          <div className="flex justify-end md:col-span-2 xl:col-span-3">
            <button type="submit" className="admin-primary-button focus-ring">
              <Plus className="size-4" aria-hidden="true" />
              Save Recommendation
            </button>
          </div>
        </form>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Search and Filter" subtitle="Locate recommendations by status or keyword." />
        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_220px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search recommendation, client, or solution" className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold" />
          </label>
          <SelectField label="Status" value={statusFilter} options={["All", ...recommendationStatusOptions]} onChange={(value) => setStatusFilter(value as "All" | (typeof recommendationStatusOptions)[number])} />
        </div>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Recommendation Records" subtitle="Open a recommendation to update the delivery path or launch the next step." />
        <div className="mt-5 grid gap-4">
          {recommendations.length ? recommendations.map((record) => <RecommendationCard key={record.id} record={record} />) : <EmptyState title="No recommendations yet" description="Your first recommendation will appear here once created from an assessment." />}
        </div>
      </section>
    </WorkspaceShell>
  );
}

export function ConsultingRecommendationDetailPage() {
  const { recommendationId } = useParams();
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();

  useEffect(() => {
    document.title = `Consulting Recommendation Detail | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const record = useMemo(
    () => (store && recommendationId ? store.recommendations.find((item) => item.id === recommendationId && item.recommendationId.startsWith("TV-REC")) || null : null),
    [recommendationId, store],
  );

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Consulting Recommendation Detail"
        description="We could not load the selected consulting recommendation record."
        detail={loadError || "The record could not be loaded from the Truevance store."}
      />
    );
  }
  if (loadStatus === "loading" || !store) return <LoadingShell title="Consulting Recommendation Detail" description="Loading consulting recommendation record." />;
  if (!record) return <NotFoundShell title="Recommendation not found" description="The requested recommendation does not exist in the current Truevance store." backHref="/admin/truevance-recommendations" backLabel="Back to Recommendations" />;
  const recommendationRecord = record;

  async function persist(nextRecord: ConsultingRecommendationRecord) {
    const response = await updateTruevanceRecommendation(nextRecord.id, nextRecord);
    if (!response?.recommendation) return;
    setStore((current) =>
      current
        ? { ...current, recommendations: current.recommendations.map((item) => (item.id === response.recommendation.id ? response.recommendation : item)) }
        : current,
    );
  }

  function updateField<K extends keyof ConsultingRecommendationRecord>(key: K, value: ConsultingRecommendationRecord[K]) {
    void persist({ ...recommendationRecord, [key]: value, updatedAt: new Date().toISOString() });
  }

  function addEvent(event: FormEvent<HTMLFormElement>, detailForm: { title: string; detail: string }) {
    event.preventDefault();
    if (!detailForm.title.trim()) return;
    void appendTruevanceRecommendationEvent(recommendationRecord.id, { title: detailForm.title, detail: detailForm.detail }).then((response) => {
      if (!response?.recommendation) return;
      setStore((current) =>
        current
          ? { ...current, recommendations: current.recommendations.map((item) => (item.id === response.recommendation.id ? response.recommendation : item)) }
          : current,
      );
    });
  }

  async function createOpportunityFromRecommendation() {
    const created = await createTruevanceOpportunity({
      companyName: recommendationRecord.client,
      contactName: "",
      email: "",
      phone: "",
      industry: "Consulting",
      leadSource: "Truevance Recommendation",
      opportunityType: recommendationRecord.serviceCategoryAlignment || "Digital Solutions Consulting",
      estimatedRevenue: 0,
      probability: 50,
      currentStage: "New Inquiry",
      assignedConsultant: TRUEVANCE_BRAND_NAME,
      nextAction: recommendationRecord.suggestedSolution || "Review recommendation and scope opportunity.",
      followUpDate: "",
      notes: recommendationRecord.notes || recommendationRecord.opportunityIdentified || "",
      status: "Active",
    });
    if (!created?.opportunity) return;
    setStore((current) =>
      current
        ? { ...current, opportunities: [created.opportunity, ...current.opportunities] }
        : { opportunities: [created.opportunity], assessments: [], recommendations: [], roadmaps: [], projects: [] },
    );
    navigate(`/admin/truevance-opportunities/${created.opportunity.id}`);
  }

  async function createRoadmapFromRecommendation() {
    const created = await createTruevanceRoadmap({
      roadmapHorizon: "Quick Wins",
      client: recommendationRecord.client,
      title: `${recommendationRecord.client} Roadmap`,
      priorityLevel: recommendationRecord.priorityLevel,
      owner: TRUEVANCE_BRAND_NAME,
      dueDate: "",
      notes: recommendationRecord.suggestedSolution,
      linkedAssessmentId: recommendationRecord.assessmentId,
      linkedAssessmentName: recommendationRecord.assessmentName,
      linkedOpportunityId: "",
      linkedOpportunityName: "",
      status: "Planned",
    });
    if (!created?.roadmap) return;
    setStore((current) =>
      current
        ? { ...current, roadmaps: [created.roadmap, ...current.roadmaps] }
        : { opportunities: [], assessments: [], recommendations: [], roadmaps: [created.roadmap], projects: [] },
    );
    navigate(`/admin/implementation-roadmaps/${created.roadmap.id}`);
  }

  return (
    <RecordDetailShell
      title="Consulting Recommendation Detail"
      eyebrow="Phase 2D"
      description="Refine the recommendation, keep the notes current, and turn it into an opportunity or roadmap when ready."
      backHref="/admin/truevance-recommendations"
      backLabel="Back to Recommendations"
      recordLabel={recommendationRecord.recommendationId}
    >
      <div className="mb-6 flex flex-wrap gap-2">
        <button onClick={() => void createOpportunityFromRecommendation()} className="admin-primary-button focus-ring">
          <Target className="size-4" aria-hidden="true" />
          Create Opportunity
        </button>
        <button onClick={() => void createRoadmapFromRecommendation()} className="admin-secondary-button focus-ring">
          <Workflow className="size-4" aria-hidden="true" />
          Create Roadmap
        </button>
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Recommendation Information" subtitle="Keep the solution direction, impact, and service alignment current." />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <TextField label="Assessment ID" value={recommendationRecord.assessmentId} onChange={(value) => updateField("assessmentId", value)} />
            <TextField label="Assessment Name" value={recommendationRecord.assessmentName} onChange={(value) => updateField("assessmentName", value)} />
            <TextField label="Client" value={recommendationRecord.client} onChange={(value) => updateField("client", value)} />
            <TextField label="Opportunity Identified" value={recommendationRecord.opportunityIdentified} onChange={(value) => updateField("opportunityIdentified", value)} />
            <TextField label="Impact Level" value={recommendationRecord.impactLevel} onChange={(value) => updateField("impactLevel", value)} />
            <SelectField label="Priority" value={recommendationRecord.priorityLevel} options={assessmentPriorityOptions} onChange={(value) => updateField("priorityLevel", value as ConsultingRecommendationRecord["priorityLevel"]) } />
            <TextField label="Estimated Effort" value={recommendationRecord.estimatedEffort} onChange={(value) => updateField("estimatedEffort", value)} />
            <TextField label="Service Category Alignment" value={recommendationRecord.serviceCategoryAlignment} onChange={(value) => updateField("serviceCategoryAlignment", value)} />
            <SelectField label="Status" value={recommendationRecord.status} options={recommendationStatusOptions} onChange={(value) => updateField("status", value as ConsultingRecommendationRecord["status"]) } />
            <TextAreaField className="md:col-span-2" label="Suggested Solution" value={recommendationRecord.suggestedSolution} onChange={(value) => updateField("suggestedSolution", value)} />
            <TextAreaField className="md:col-span-2" label="Notes" value={recommendationRecord.notes} onChange={(value) => updateField("notes", value)} />
          </div>
        </article>
        <TimelinePanel title="Recommendation Timeline" subtitle="Log recommendation handoffs and notes." events={record.timelineEvents} onAdd={addEvent} />
      </div>
    </RecordDetailShell>
  );
}

export function ImplementationRoadmapEnginePage() {
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | (typeof roadmapStatusOptions)[number]>("All");
  const [form, setForm] = useState(createBlankRoadmapForm());

  useEffect(() => {
    document.title = `Implementation Roadmaps | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const roadmaps = useMemo(() => {
    if (!store) return [];
    const normalizedQuery = query.trim().toLowerCase();
    return store.roadmaps.filter((record) => {
      const matchesStatus = statusFilter === "All" || record.status === statusFilter;
      const haystack = flattenRecordText(record.roadmapId, record.title, record.client, record.owner, record.notes, record.linkedAssessmentName, record.linkedOpportunityName);
      return matchesStatus && (!normalizedQuery || haystack.includes(normalizedQuery));
    });
  }, [query, statusFilter, store]);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleCreateRoadmap(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const created = await createTruevanceRoadmap({
      roadmapHorizon: form.roadmapHorizon,
      client: form.client,
      title: form.title,
      priorityLevel: form.priorityLevel,
      owner: form.owner,
      dueDate: form.dueDate,
      notes: form.notes,
      linkedAssessmentId: form.linkedAssessmentId,
      linkedAssessmentName: form.linkedAssessmentName,
      linkedOpportunityId: form.linkedOpportunityId,
      linkedOpportunityName: form.linkedOpportunityName,
      status: form.status,
    });
    if (!created?.roadmap) return;
    setStore((current) =>
      current
        ? { ...current, roadmaps: [created.roadmap, ...current.roadmaps] }
        : { opportunities: [], assessments: [], recommendations: [], roadmaps: [created.roadmap], projects: [] },
    );
    setForm(createBlankRoadmapForm());
    navigate(`/admin/implementation-roadmaps/${created.roadmap.id}`);
  }

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Implementation Roadmaps"
        description="We could not load the implementation roadmap workspace from the production data source."
        detail={loadError || "The workspace fetch failed before the roadmap records could load."}
      />
    );
  }
  if (loadStatus === "loading" || !store) return <LoadingShell title="Implementation Roadmaps" description="Loading implementation roadmap records." />;

  return (
    <WorkspaceShell
      title="Implementation Roadmaps"
      eyebrow="Phase 2D"
      description="Plan quick wins, short-term projects, mid-term projects, and long-term initiatives without duplicating client context."
      icon={Workflow}
      metrics={[
        { label: "Roadmaps", value: store.roadmaps.length, note: "Planned implementation pathways." },
        { label: "Implementation Progress", value: formatPercent(calculateTruevanceMetrics(store).implementationProgress), note: "Average roadmap completion signal." },
        { label: "Active Projects", value: calculateTruevanceMetrics(store).activeProjects, note: "Current active consulting projects." },
        { label: "Completed Projects", value: calculateTruevanceMetrics(store).completedProjects, note: "Finished delivery records." },
      ]}
      actions={
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/consulting-assessments" className="admin-secondary-button focus-ring">
            Assessments
          </Link>
          <Link to="/admin/truevance-recommendations" className="admin-secondary-button focus-ring">
            Recommendations
          </Link>
          <Link to="/admin/truevance-projects" className="admin-secondary-button focus-ring">
            Projects
          </Link>
        </div>
      }
    >
      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Create Roadmap" subtitle="Capture the implementation horizon, owners, due dates, and linked records." />
        <form onSubmit={handleCreateRoadmap} className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <SelectField label="Roadmap Horizon" value={form.roadmapHorizon} options={roadmapHorizonOptions} onChange={(value) => updateForm("roadmapHorizon", value as (typeof roadmapHorizonOptions)[number])} />
          <TextField label="Client" required value={form.client} onChange={(value) => updateForm("client", value)} />
          <TextField label="Title" value={form.title} onChange={(value) => updateForm("title", value)} />
          <SelectField label="Priority" value={form.priorityLevel} options={assessmentPriorityOptions} onChange={(value) => updateForm("priorityLevel", value as (typeof assessmentPriorityOptions)[number])} />
          <TextField label="Owner" value={form.owner} onChange={(value) => updateForm("owner", value)} />
          <TextField label="Due Date" type="date" value={form.dueDate} onChange={(value) => updateForm("dueDate", value)} />
          <TextField label="Linked Assessment ID" value={form.linkedAssessmentId} onChange={(value) => updateForm("linkedAssessmentId", value)} />
          <TextField label="Linked Assessment Name" value={form.linkedAssessmentName} onChange={(value) => updateForm("linkedAssessmentName", value)} />
          <TextField label="Linked Opportunity ID" value={form.linkedOpportunityId} onChange={(value) => updateForm("linkedOpportunityId", value)} />
          <TextField label="Linked Opportunity Name" value={form.linkedOpportunityName} onChange={(value) => updateForm("linkedOpportunityName", value)} />
          <SelectField label="Status" value={form.status} options={roadmapStatusOptions} onChange={(value) => updateForm("status", value as (typeof roadmapStatusOptions)[number])} />
          <TextAreaField className="md:col-span-2 xl:col-span-3" label="Notes" value={form.notes} onChange={(value) => updateForm("notes", value)} />
          <div className="flex justify-end md:col-span-2 xl:col-span-3">
            <button type="submit" className="admin-primary-button focus-ring">
              <Plus className="size-4" aria-hidden="true" />
              Save Roadmap
            </button>
          </div>
        </form>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Search and Filter" subtitle="Narrow the roadmap list by status or keyword." />
        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_220px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search client, title, or linked record" className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold" />
          </label>
          <SelectField label="Status" value={statusFilter} options={["All", ...roadmapStatusOptions]} onChange={(value) => setStatusFilter(value as "All" | (typeof roadmapStatusOptions)[number])} />
        </div>
      </section>

      <section className="command-center-card rounded-lg p-5 sm:p-6">
        <SectionHeading title="Roadmap Records" subtitle="Open a roadmap to update phases, linked records, and delivery milestones." />
        <div className="mt-5 grid gap-4">
          {roadmaps.length ? roadmaps.map((record) => <RoadmapCard key={record.id} record={record} />) : <EmptyState title="No roadmaps yet" description="Your first roadmap will appear here once created." />}
        </div>
      </section>
    </WorkspaceShell>
  );
}

export function ImplementationRoadmapDetailPage() {
  const { roadmapId } = useParams();
  const navigate = useNavigate();
  const [store, setStore, loadStatus, loadError] = useTruevanceWorkspaceStore();

  useEffect(() => {
    document.title = `Implementation Roadmap Detail | ${TRUEVANCE_BRAND_NAME}`;
  }, []);

  const record = useMemo(
    () => (store && roadmapId ? store.roadmaps.find((item) => item.id === roadmapId && item.roadmapId.startsWith("TV-RDM")) || null : null),
    [roadmapId, store],
  );

  if (loadStatus === "error") {
    return (
      <WorkspaceErrorShell
        title="Implementation Roadmap Detail"
        description="We could not load the selected implementation roadmap record."
        detail={loadError || "The record could not be loaded from the Truevance store."}
      />
    );
  }
  if (loadStatus === "loading" || !store) return <LoadingShell title="Implementation Roadmap Detail" description="Loading roadmap record." />;
  if (!record) return <NotFoundShell title="Roadmap not found" description="The requested roadmap does not exist in the current Truevance store." backHref="/admin/implementation-roadmaps" backLabel="Back to Roadmaps" />;
  const roadmapRecord = record;

  async function persist(nextRecord: ImplementationRoadmapRecord) {
    const response = await updateTruevanceRoadmap(nextRecord.id, nextRecord);
    if (!response?.roadmap) return;
    setStore((current) =>
      current
        ? { ...current, roadmaps: current.roadmaps.map((item) => (item.id === response.roadmap.id ? response.roadmap : item)) }
        : current,
    );
  }

  function updateField<K extends keyof ImplementationRoadmapRecord>(key: K, value: ImplementationRoadmapRecord[K]) {
    void persist({ ...roadmapRecord, [key]: value, updatedAt: new Date().toISOString() });
  }

  function addEvent(event: FormEvent<HTMLFormElement>, detailForm: { title: string; detail: string }) {
    event.preventDefault();
    if (!detailForm.title.trim()) return;
    void appendTruevanceRoadmapEvent(roadmapRecord.id, { title: detailForm.title, detail: detailForm.detail }).then((response) => {
      if (!response?.roadmap) return;
      setStore((current) =>
        current
          ? { ...current, roadmaps: current.roadmaps.map((item) => (item.id === response.roadmap.id ? response.roadmap : item)) }
          : current,
      );
    });
  }

  async function createProjectFromRoadmap() {
    const created = await createTruevanceProject({
      client: roadmapRecord.client,
      startDate: new Date().toISOString().slice(0, 10),
      currentPhase: "Discovery",
      completionPercent: 0,
      revenue: 0,
      assignedTeam: roadmapRecord.owner || TRUEVANCE_BRAND_NAME,
      notes: roadmapRecord.notes || roadmapRecord.title,
      status: "Active",
    });
    if (!created?.project) return;
    setStore((current) =>
      current
        ? { ...current, projects: [created.project, ...current.projects] }
        : { opportunities: [], assessments: [], recommendations: [], roadmaps: [], projects: [created.project] },
    );
    navigate(`/admin/truevance-projects/${created.project.id}`);
  }

  return (
    <RecordDetailShell
      title="Implementation Roadmap Detail"
      eyebrow="Phase 2D"
      description="Keep the roadmap current and launch a consulting project when the plan is ready to execute."
      backHref="/admin/implementation-roadmaps"
      backLabel="Back to Roadmaps"
      recordLabel={roadmapRecord.roadmapId}
    >
      <div className="mb-6 flex flex-wrap gap-2">
        <button onClick={() => void createProjectFromRoadmap()} className="admin-primary-button focus-ring">
          <Layers3 className="size-4" aria-hidden="true" />
          Create Project
        </button>
        <Link to="/admin/truevance-projects" className="admin-secondary-button focus-ring">
          View Projects
        </Link>
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <article className="command-center-card rounded-lg p-5 sm:p-6">
          <SectionHeading title="Roadmap Information" subtitle="Update the implementation horizon and linked record context." />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <SelectField label="Roadmap Horizon" value={roadmapRecord.roadmapHorizon} options={roadmapHorizonOptions} onChange={(value) => updateField("roadmapHorizon", value as ImplementationRoadmapRecord["roadmapHorizon"]) } />
            <TextField label="Client" value={roadmapRecord.client} onChange={(value) => updateField("client", value)} />
            <TextField label="Title" value={roadmapRecord.title} onChange={(value) => updateField("title", value)} />
            <SelectField label="Priority" value={roadmapRecord.priorityLevel} options={assessmentPriorityOptions} onChange={(value) => updateField("priorityLevel", value as ImplementationRoadmapRecord["priorityLevel"]) } />
            <TextField label="Owner" value={roadmapRecord.owner} onChange={(value) => updateField("owner", value)} />
            <TextField label="Due Date" type="date" value={roadmapRecord.dueDate} onChange={(value) => updateField("dueDate", value)} />
            <TextField label="Linked Assessment ID" value={roadmapRecord.linkedAssessmentId} onChange={(value) => updateField("linkedAssessmentId", value)} />
            <TextField label="Linked Assessment Name" value={roadmapRecord.linkedAssessmentName} onChange={(value) => updateField("linkedAssessmentName", value)} />
            <TextField label="Linked Opportunity ID" value={roadmapRecord.linkedOpportunityId} onChange={(value) => updateField("linkedOpportunityId", value)} />
            <TextField label="Linked Opportunity Name" value={roadmapRecord.linkedOpportunityName} onChange={(value) => updateField("linkedOpportunityName", value)} />
            <SelectField label="Status" value={roadmapRecord.status} options={roadmapStatusOptions} onChange={(value) => updateField("status", value as ImplementationRoadmapRecord["status"]) } />
            <TextAreaField className="md:col-span-2" label="Notes" value={roadmapRecord.notes} onChange={(value) => updateField("notes", value)} />
          </div>
        </article>
        <TimelinePanel title="Roadmap Timeline" subtitle="Log roadmap updates and execution milestones." events={record.timelineEvents} onAdd={addEvent} />
      </div>
    </RecordDetailShell>
  );
}

function ConsultingAssessmentCard({ record }: { record: BusinessAssessmentRecord }) {
  return (
    <article className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)] lg:grid-cols-[1.25fr_.8fr_.7fr_.7fr_auto] lg:items-center">
      <div>
        <h3 className="font-display text-2xl font-bold text-[#050505]">{record.assessmentName || record.client}</h3>
        <p className="mt-1 text-sm font-semibold text-[#5c5246]">{record.client} · {record.assessmentId}</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{record.assessmentCategory}</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{record.status}</p>
        <p className="mt-1 text-[#5c5246]">{record.priorityLevel} priority</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{formatDisplayDate(record.assessmentDate)}</p>
        <p className="mt-1 text-[#5c5246]">{record.assignedConsultant}</p>
      </div>
      <div className="text-sm font-bold text-[#5c5246]">
        <p>Questions</p>
        <p className="mt-1 text-[#332a1f]">{record.assessmentQuestions.length}</p>
      </div>
      <Link to={`/admin/consulting-assessments/${record.id}`} className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]">
        View Record
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function RecommendationCard({ record }: { record: ConsultingRecommendationRecord }) {
  return (
    <article className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)] lg:grid-cols-[1.25fr_.8fr_.7fr_.7fr_auto] lg:items-center">
      <div>
        <h3 className="font-display text-2xl font-bold text-[#050505]">{record.assessmentName || record.client}</h3>
        <p className="mt-1 text-sm font-semibold text-[#5c5246]">{record.client} · {record.recommendationId}</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{record.serviceCategoryAlignment || "Unassigned service category"}</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{record.status}</p>
        <p className="mt-1 text-[#5c5246]">{record.priorityLevel} priority</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{record.impactLevel || "Impact not set"}</p>
        <p className="mt-1 text-[#5c5246]">{record.estimatedEffort || "Effort not set"}</p>
      </div>
      <div className="text-sm font-bold text-[#5c5246]">
        <p>Opportunity</p>
        <p className="mt-1 text-[#332a1f]">{record.opportunityIdentified || "None set"}</p>
      </div>
      <Link to={`/admin/truevance-recommendations/${record.id}`} className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]">
        View Record
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function RoadmapCard({ record }: { record: ImplementationRoadmapRecord }) {
  return (
    <article className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)] lg:grid-cols-[1.25fr_.8fr_.7fr_.7fr_auto] lg:items-center">
      <div>
        <h3 className="font-display text-2xl font-bold text-[#050505]">{record.title}</h3>
        <p className="mt-1 text-sm font-semibold text-[#5c5246]">{record.client} · {record.roadmapId}</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{record.roadmapHorizon}</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{record.status}</p>
        <p className="mt-1 text-[#5c5246]">{record.priorityLevel} priority</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{record.owner || "Owner not set"}</p>
        <p className="mt-1 text-[#5c5246]">{formatDisplayDate(record.dueDate)}</p>
      </div>
      <div className="text-sm font-bold text-[#5c5246]">
        <p>Linked</p>
        <p className="mt-1 text-[#332a1f]">{record.linkedAssessmentName || "None"}</p>
      </div>
      <Link to={`/admin/implementation-roadmaps/${record.id}`} className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]">
        View Record
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function parseAssessmentQuestions(value: string) {
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseAssessmentScores(value: string) {
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const [areaPart, scorePart] = item.split(/[:|-]/);
      return {
        area: areaPart?.trim() || "Area",
        score: Number(scorePart?.trim() || 0),
      };
    });
}

function formatAssessmentQuestions(value: string[]) {
  return value.join("\n");
}

function formatAssessmentScores(value: Array<{ area: string; score: number }>) {
  return value.map((item) => `${item.area}: ${item.score}`).join("\n");
}

function flattenRecordText(...values: string[]) {
  return values.filter(Boolean).join(" ").toLowerCase();
}
